import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { server } from "../../server";
import { useReactToPrint } from "react-to-print";
import useAuth from "../../useAuth";
import {
  FaPrint,
  FaAward,
  FaSearch,
  FaSave,
  FaCheckCircle,
  FaTimes,
  FaUser,
  FaBuilding,
  FaLayerGroup,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import PrintableExperienceCertificate from "./PrintableExperienceCertificate";

export default function GenerateExperienceCertificate() {
  const todayStr = new Date().toISOString().split("T")[0];

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const navigate = useNavigate();

  // Search & Filter States (Only Department & Section)
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("ALL");
  const [selectedSection, setSelectedSection] = useState("ALL");

  // Form State
  const [formData, setFormData] = useState({
    joiningDate: todayStr,
    relievingDate: todayStr,
    issueDate: todayStr,
    designation: "",
    conduct: "Good",
    reasonForLeaving: "",
    remarks: "",
  });

  const [showPreview, setShowPreview] = useState(false);
  const [generatedCert, setGeneratedCert] = useState(null);

  // 1. Debounce Search Input (300ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchInput.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Fetch employees list
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${server}/employees`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEmployees(res.data.data || []);
      } catch (error) {
        toast.error("Failed to load employees");
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchEmployees();
  }, [token]);

  // 2. Extract unique departments & sections for filters
  const departmentsList = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => {
      const deptName = e.department?.name || e.Department?.Name;
      if (deptName) set.add(deptName);
    });
    return Array.from(set).sort();
  }, [employees]);

  const sectionsList = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => {
      if (e.section) set.add(e.section);
    });
    return Array.from(set).sort();
  }, [employees]);

  // 3. Filtered Employees (Department & Section only)
  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      const matchSearch =
        !debouncedSearchTerm ||
        emp.name?.toLowerCase().includes(debouncedSearchTerm) ||
        emp.employeeCode?.toLowerCase().includes(debouncedSearchTerm) ||
        emp.designation?.toLowerCase().includes(debouncedSearchTerm) ||
        emp.mobileNo?.toLowerCase().includes(debouncedSearchTerm);

      const empDept = emp.department?.name || emp.Department?.Name || "Unassigned";
      const matchDept =
        selectedDepartment === "ALL" || empDept === selectedDepartment;

      const matchSection =
        selectedSection === "ALL" || (emp.section || "N/A") === selectedSection;

      return matchSearch && matchDept && matchSection;
    });
  }, [employees, debouncedSearchTerm, selectedDepartment, selectedSection]);

  const hasActiveFilters =
    searchInput !== "" ||
    selectedDepartment !== "ALL" ||
    selectedSection !== "ALL";

  const handleClearFilters = () => {
    setSearchInput("");
    setSelectedDepartment("ALL");
    setSelectedSection("ALL");
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePreview = (e) => {
    e.preventDefault();
    if (!selectedEmployee) return toast.error("Please select an employee!");
    if (formData.joiningDate > todayStr)
      return toast.error("Joining Date cannot be in the future!");
    if (formData.relievingDate > todayStr)
      return toast.error("Relieving Date cannot be in the future!");
    if (formData.issueDate > todayStr)
      return toast.error("Issue Date cannot be in the future!");
    setShowPreview(true);
  };

  const submitCertificate = async () => {
    if (!selectedEmployee) return toast.error("Please select an employee!");

    try {
      const payload = {
        employeeId: selectedEmployee.id,
        joiningDate: formData.joiningDate,
        relievingDate: formData.relievingDate,
        issueDate: formData.issueDate,
        designation: formData.designation || selectedEmployee.designation,
        conduct: formData.conduct,
        reasonForLeaving: formData.reasonForLeaving,
        remarks: formData.remarks,
      };

      const res = await axios.post(
        `${server}/experienceCertificates/register`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        toast.success("Experience Certificate Generated Successfully!");
        const mappedCert = {
          ...res.data.data,
          employee: selectedEmployee,
        };
        setGeneratedCert(mappedCert);
        setShowPreview(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to generate Experience Certificate");
    }
  };

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `ExpCert_${selectedEmployee?.name || ""}_${generatedCert?.certificateNumber || ""}`,
    pageStyle: `@page { size: A4 portrait; margin: 10mm; }`,
  });

  const previewCert = generatedCert || {
    employee: selectedEmployee,
    certificateNumber: "EXP-DRAFT-XXXX",
    joiningDate: formData.joiningDate,
    relievingDate: formData.relievingDate,
    issueDate: formData.issueDate,
    designation: formData.designation || selectedEmployee?.designation,
    conduct: formData.conduct,
    reasonForLeaving: formData.reasonForLeaving,
    remarks: formData.remarks,
  };

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Control Panel: Hidden during print */}
      <div className="print:hidden">
        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
              <FaAward size={18} />
            </div>
            <div>
              <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">Issue Staff Experience Certificate</h1>
              <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Select an employee, fill out service details, and generate their official Experience Certificate</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/my/employees/experience-certificates")}
              className="bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-300 px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs"
            >
              View Issued List
            </button>
            {generatedCert && (
              <button
                onClick={handlePrint}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
              >
                <FaPrint size={11} /> Print Official Certificate
              </button>
            )}
          </div>
        </div>

        {!generatedCert && !showPreview ? (
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-800 p-8 grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            
            {/* SECTION 1: Select Employee (Department & Section Filters Only) */}
            <div className="lg:col-span-6 space-y-6">
              <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-800 pb-4">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                  <span>1. Select Employee</span>
                  <span className="text-xs font-bold bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 px-2.5 py-1 rounded-full">
                    {filteredEmployees.length} Available
                  </span>
                </h3>

                {hasActiveFilters && (
                  <button
                    onClick={handleClearFilters}
                    className="text-xs font-bold text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 flex items-center gap-1 bg-red-50 dark:bg-red-950/40 px-3 py-1.5 rounded-xl transition"
                  >
                    <FaTimes size={12} /> Clear Filters
                  </button>
                )}
              </div>

              {/* Debounced Search Bar */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-2">
                  Search Employee
                </label>
                <div className="relative">
                  <FaSearch className="absolute left-3.5 top-3.5 text-gray-400 dark:text-slate-500" />
                  <input
                    type="text"
                    placeholder="Search by Name, Code, Designation, Mobile..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full pl-10 pr-10 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900 focus:border-blue-500 dark:focus:border-blue-500 outline-none transition-all font-medium text-gray-800 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 text-sm"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={() => setSearchInput("")}
                      className="absolute right-3.5 top-3.5 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>

              {/* Filter Controls Grid: Department & Section Only */}
              <div className="grid grid-cols-2 gap-3 bg-gray-50/70 dark:bg-slate-800/40 p-4 rounded-2xl border border-gray-100 dark:border-slate-800">
                {/* Department Filter */}
                <div>
                  <label className="block text-[11px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
                    <FaBuilding size={10} /> Department
                  </label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-700 dark:text-slate-200 outline-none focus:border-blue-500"
                  >
                    <option value="ALL">All Departments</option>
                    {departmentsList.map((dept, idx) => (
                      <option key={idx} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Section Filter */}
                <div>
                  <label className="block text-[11px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
                    <FaLayerGroup size={10} /> Section
                  </label>
                  <select
                    value={selectedSection}
                    onChange={(e) => setSelectedSection(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-700 dark:text-slate-200 outline-none focus:border-blue-500"
                  >
                    <option value="ALL">All Sections</option>
                    {sectionsList.map((sec, idx) => (
                      <option key={idx} value={sec}>
                        {sec}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Direct Filtered Employees List */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">
                    Filtered Employees ({filteredEmployees.length})
                  </label>
                  <span className="text-[11px] text-gray-400 dark:text-slate-500 font-medium">
                    Click an employee to select
                  </span>
                </div>

                <div className="max-h-80 overflow-y-auto space-y-2 pr-1 custom-scrollbar border border-gray-100 dark:border-slate-800 p-2 rounded-2xl bg-gray-50/50 dark:bg-slate-800/30">
                  {filteredEmployees.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 dark:text-slate-500 bg-white dark:bg-slate-800/60 rounded-xl border border-dashed border-gray-200 dark:border-slate-700">
                      <FaUser className="text-3xl mx-auto mb-2 opacity-40 text-gray-400 dark:text-slate-500" />
                      <p className="text-sm font-bold text-gray-600 dark:text-slate-300">No employees match filters</p>
                      <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Try clearing or adjusting your search & filters.</p>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={handleClearFilters}
                          className="mt-3 px-3 py-1.5 bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 font-bold text-xs rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900 transition"
                        >
                          Clear All Filters
                        </button>
                      )}
                    </div>
                  ) : (
                    filteredEmployees.map((emp) => {
                      const isSelected = selectedEmployee?.id === emp.id;
                      const deptName = emp.department?.name || emp.Department?.Name || "No Dept";
                      return (
                        <div
                          key={emp.id}
                          onClick={() => {
                            setSelectedEmployee(emp);
                            setGeneratedCert(null);
                            setShowPreview(false);
                            const formattedJoining = emp.joiningDate ? emp.joiningDate.split("T")[0] : todayStr;
                            setFormData((prev) => ({
                              ...prev,
                              joiningDate: formattedJoining,
                              designation: emp.designation || "",
                            }));
                          }}
                          className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between group ${
                            isSelected
                              ? "bg-blue-600 dark:bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/20 ring-2 ring-blue-300 dark:ring-blue-500/50"
                              : "bg-white dark:bg-slate-800/90 text-gray-800 dark:text-slate-100 border-gray-200 dark:border-slate-700/80 hover:border-blue-300 dark:hover:border-blue-500/50 hover:bg-blue-50/40 dark:hover:bg-slate-700/60"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-xl font-bold flex items-center justify-center text-sm ${
                                isSelected
                                  ? "bg-white/20 text-white"
                                  : "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 group-hover:bg-blue-200 dark:group-hover:bg-blue-800/60"
                              }`}
                            >
                              {emp.profilePictureUrl ? (
                                <img
                                  src={emp.profilePictureUrl}
                                  alt={emp.name}
                                  className="w-full h-full object-cover rounded-xl"
                                />
                              ) : (
                                (emp.name || "E").charAt(0).toUpperCase()
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className={`font-bold text-sm ${isSelected ? "text-white" : "text-gray-900 dark:text-white"}`}>
                                  {emp.name}
                                </span>
                                <span
                                  className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                                    isSelected
                                      ? "bg-white/20 text-white"
                                      : "bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300"
                                  }`}
                                >
                                  {emp.employeeCode}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5 text-xs">
                                <span className={isSelected ? "text-blue-100" : "text-gray-500 dark:text-slate-400"}>
                                  {emp.designation || "Staff"}
                                </span>
                                <span className={isSelected ? "text-blue-200" : "text-gray-300 dark:text-slate-600"}>•</span>
                                <span className={isSelected ? "text-blue-100" : "text-gray-500 dark:text-slate-400"}>
                                  {deptName}
                                </span>
                                {emp.section && (
                                  <>
                                    <span className={isSelected ? "text-blue-200" : "text-gray-300 dark:text-slate-600"}>•</span>
                                    <span className={isSelected ? "text-blue-100" : "text-gray-500 dark:text-slate-400"}>
                                      Sec: {emp.section}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {isSelected ? (
                              <FaCheckCircle className="text-white text-lg animate-pulse" />
                            ) : (
                              <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity">
                                Select →
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Selected Profile Summary Card */}
              {selectedEmployee && (
                <div className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-900 border border-blue-200 dark:border-blue-900/60 rounded-2xl shadow-sm relative">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-blue-600 text-white font-black flex items-center justify-center text-lg shadow-md">
                        {selectedEmployee.profilePictureUrl ? (
                          <img
                            src={selectedEmployee.profilePictureUrl}
                            alt={selectedEmployee.name}
                            className="w-full h-full object-cover rounded-2xl"
                          />
                        ) : (
                          (selectedEmployee.name || "E").charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <h4 className="font-extrabold text-blue-950 dark:text-white text-base">
                          {selectedEmployee.name}
                        </h4>
                        <span className="text-xs font-bold text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/60 px-2 py-0.5 rounded-full">
                          {selectedEmployee.employeeCode}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedEmployee(null)}
                      className="text-xs text-red-500 dark:text-red-400 font-bold hover:underline"
                    >
                      Deselect
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-y-2 text-xs border-t border-blue-200/60 dark:border-slate-700/60 pt-3">
                    <span className="text-gray-500 dark:text-slate-400 font-medium">Designation:</span>
                    <span className="font-bold text-gray-800 dark:text-slate-200">
                      {selectedEmployee.designation || "N/A"}
                    </span>
                    <span className="text-gray-500 dark:text-slate-400 font-medium">Department:</span>
                    <span className="font-bold text-gray-800 dark:text-slate-200">
                      {selectedEmployee.department?.name || "N/A"}
                    </span>
                    <span className="text-gray-500 dark:text-slate-400 font-medium">Section:</span>
                    <span className="font-bold text-gray-800 dark:text-slate-200">
                      {selectedEmployee.section || "N/A"}
                    </span>
                    <span className="text-gray-500 dark:text-slate-400 font-medium">Joining Date:</span>
                    <span className="font-bold text-gray-800 dark:text-slate-200">
                      {selectedEmployee.joiningDate ? selectedEmployee.joiningDate.split("T")[0] : "N/A"}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 2: Certificate Details Form */}
            <form onSubmit={handlePreview} className="lg:col-span-6 space-y-6">
              <h3 className="text-lg font-bold text-gray-800 dark:text-white border-b border-gray-100 dark:border-slate-800 pb-4">
                2. Certificate Details
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Joining Date
                  </label>
                  <input
                    type="date"
                    name="joiningDate"
                    required
                    max={todayStr}
                    value={formData.joiningDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all font-medium text-gray-700 dark:text-slate-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Relieving Date
                  </label>
                  <input
                    type="date"
                    name="relievingDate"
                    required
                    max={todayStr}
                    value={formData.relievingDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all font-medium text-gray-700 dark:text-slate-200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Issue Date
                  </label>
                  <input
                    type="date"
                    name="issueDate"
                    required
                    max={todayStr}
                    value={formData.issueDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all font-medium text-gray-700 dark:text-slate-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Designation
                  </label>
                  <input
                    type="text"
                    name="designation"
                    placeholder="e.g. Senior Teacher"
                    value={formData.designation}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all font-medium text-gray-700 dark:text-slate-200 placeholder-gray-400 dark:placeholder-slate-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Conduct / Performance
                  </label>
                  <select
                    name="conduct"
                    value={formData.conduct}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all appearance-none font-medium text-gray-700 dark:text-slate-200"
                  >
                    <option value="Excellent">Excellent</option>
                    <option value="Very Good">Very Good</option>
                    <option value="Good">Good</option>
                    <option value="Satisfactory">Satisfactory</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Reason for Leaving
                  </label>
                  <input
                    type="text"
                    name="reasonForLeaving"
                    placeholder="e.g. Resignation, Career growth"
                    value={formData.reasonForLeaving}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all font-medium text-gray-700 dark:text-slate-200 placeholder-gray-400 dark:placeholder-slate-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                  Remarks / Additional Notes
                </label>
                <input
                  type="text"
                  name="remarks"
                  placeholder="Any extra appreciation or remarks..."
                  value={formData.remarks}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all font-medium text-gray-700 dark:text-slate-200 placeholder-gray-400 dark:placeholder-slate-500"
                />
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={!selectedEmployee}
                  className={`w-full py-4 rounded-2xl flex items-center justify-center font-bold text-lg transition-all ${
                    !selectedEmployee
                      ? "bg-gray-200 dark:bg-slate-800 text-gray-400 dark:text-slate-600 cursor-not-allowed"
                      : "bg-blue-600 dark:bg-blue-600 text-white hover:bg-blue-700 dark:hover:bg-blue-500 shadow-xl shadow-blue-500/20"
                  }`}
                >
                  <FaSave className="mr-3" /> Preview Certificate
                </button>
              </div>
            </form>
          </div>
        ) : generatedCert ? (
          <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/60 rounded-3xl p-6 mb-12 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-4">
              <FaCheckCircle className="text-4xl text-emerald-500 dark:text-emerald-400" />
              <div>
                <h3 className="text-xl font-extrabold text-emerald-900 dark:text-emerald-200">
                  Experience Certificate successfully generated!
                </h3>
                <p className="text-emerald-700 dark:text-emerald-400 font-medium text-sm">
                  Cert No:{" "}
                  <span className="bg-emerald-200 dark:bg-emerald-900 px-2 py-0.5 rounded text-emerald-900 dark:text-emerald-100 mx-1">
                    {generatedCert?.certificateNumber}
                  </span>{" "}
                  is ready for printing.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setGeneratedCert(null);
                setShowPreview(false);
              }}
              className="text-gray-500 dark:text-slate-400 font-bold hover:text-gray-800 dark:hover:text-white underline text-sm"
            >
              Generate another
            </button>
          </div>
        ) : null}
      </div>

      {/* Printable Area */}
      {(showPreview || generatedCert) && (
        <div className="mt-12 print:mt-0">
          {showPreview && !generatedCert && (
            <div className="print:hidden flex flex-col md:flex-row justify-between items-center bg-yellow-50 dark:bg-amber-950/40 border border-yellow-200 dark:border-amber-900/60 p-6 rounded-3xl mb-8 shadow-sm">
              <div className="mb-4 md:mb-0">
                <h3 className="text-xl font-bold text-yellow-800 dark:text-amber-300">
                  Preview Mode
                </h3>
                <p className="text-yellow-700 dark:text-amber-400 text-sm mt-1">
                  Review the certificate details below. Click Confirm to finalize and generate the official Certificate Number.
                </p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-6 py-2.5 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 font-bold rounded-2xl border border-gray-300 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-700 transition shadow-sm"
                >
                  Back to Edit
                </button>
                <button
                  onClick={submitCertificate}
                  className="px-6 py-2.5 bg-blue-600 dark:bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 dark:hover:bg-blue-500 transition shadow-lg shadow-blue-500/20 flex items-center gap-2"
                >
                  <FaCheckCircle /> Confirm & Generate
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-center mb-20 bg-gray-50 dark:bg-slate-950 print:bg-white overflow-hidden">
            <PrintableExperienceCertificate ref={componentRef} cert={previewCert} />
          </div>
        </div>
      )}
    </div>
  );
}
